import React, { useState, useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { useAuth } from "@/App";
import Layout from "@/components/Layout";
import { RiskBadge } from "@/components/RiskBadge";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  ArrowLeft, Download, Ship, Building2, Shield, CheckCircle,
  AlertTriangle, XCircle, Flag, Loader2, FileText, Globe,
  Award, Anchor, Info, Printer
} from "lucide-react";
import { toast } from "sonner";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { saveAs } from "file-saver";

const SectionHeader = ({ number, title, icon: Icon }) => (
  <div className="flex items-center gap-3 border-b-2 border-[#0b3e65] pb-2 mb-6">
    {Icon && <Icon className="w-5 h-5 text-[#0b3e65]" />}
    <h2 className="text-lg font-serif text-[#0b3e65]">{number}. {title}</h2>
  </div>
);

const StatusBadge = ({ status, isPositive = true }) => (
  <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-2 py-1 rounded ${
    isPositive ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"
  }`}>
    {isPositive ? <CheckCircle className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
    {status}
  </span>
);

const DataRow = ({ label, value, className = "" }) => (
  <div className={`flex justify-between py-2 border-b border-slate-100 last:border-0 ${className}`}>
    <span className="text-sm text-slate-500">{label}</span>
    <span className="text-sm font-medium text-slate-800">{value || "N/A"}</span>
  </div>
);

const ReportViewPage = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchReport();
  }, [id]);

  const fetchReport = async () => {
    try {
      const res = await axios.get(`${API}/reports/${id}`);
      setReport(res.data);
    } catch (error) {
      toast.error("Failed to load report");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleDateString("en-GB", {
      day: "2-digit", month: "short", year: "numeric",
    });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleDateString("en-GB", {
      day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit"
    });
  };

  const handleExportPDF = () => {
    if (!report) return;
    const doc = new jsPDF();
    const pw = doc.internal.pageSize.getWidth();
    const ph = doc.internal.pageSize.getHeight();
    const m = 15;

    const addFooter = () => {
      const pages = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pages; i++) {
        doc.setPage(i);
        doc.setDrawColor(11, 62, 101);
        doc.line(m, ph - 15, pw - m, ph - 15);
        doc.setFontSize(7);
        doc.setTextColor(128, 128, 128);
        doc.text("GREEN BUNKERS COMPLIANCE - This report is for informational purposes. Verify all data with official sources.", pw / 2, ph - 10, { align: "center" });
        doc.text(`Page ${i} of ${pages}`, pw - m, ph - 10, { align: "right" });
      }
    };

    const checkPageBreak = (y, needed = 30) => {
      if (y > ph - needed - 20) {
        doc.addPage();
        return 20;
      }
      return y;
    };

    // === PAGE 1: TITLE PAGE ===
    doc.setFillColor(11, 62, 101);
    doc.rect(0, 0, pw, ph, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(36);
    doc.setFont("helvetica", "bold");
    doc.text(report.vessel_data?.vessel_name || "Vessel", pw / 2, ph / 2 - 20, { align: "center" });
    doc.setFontSize(16);
    doc.setFont("helvetica", "normal");
    doc.text("Risk & compliance report", pw / 2, ph / 2, { align: "center" });
    doc.setFontSize(12);
    doc.text(formatDate(report.created_at), pw / 2, ph / 2 + 15, { align: "center" });
    doc.setFontSize(10);
    doc.setTextColor(243, 112, 34);
    doc.text("GREEN BUNKERS", pw / 2, ph - 40, { align: "center" });
    doc.setTextColor(180, 180, 180);
    doc.setFontSize(8);
    doc.text("Compliance Tool", pw / 2, ph - 33, { align: "center" });

    // === PAGE 2: GENERAL INFO ===
    doc.addPage();
    let y = 20;
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(11, 62, 101);
    doc.text("General information", m, y);
    y += 12;

    const generalInfo = [
      ["Vessel", report.vessel_data?.vessel_name || "N/A"],
      ["Sanction status", report.vessel_screening?.risk_level === "GREEN" ? "Not a sanctioned vessel" : report.vessel_screening?.risk_level === "RED" ? "Potential sanctions match" : "Under investigation"],
      ["Investigation timeframe", report.investigation_timeframe || "24 months"],
      ["Report created by", report.created_by_email || user?.email || "N/A"],
      ["Report date", formatDateTime(report.created_at)],
    ];
    autoTable(doc, {
      startY: y, body: generalInfo, theme: "plain",
      styles: { fontSize: 10, cellPadding: 5 },
      columnStyles: { 0: { fontStyle: "bold", cellWidth: 50, textColor: [100, 116, 139] }, 1: { cellWidth: 110 } },
      margin: { left: m },
    });
    y = doc.lastAutoTable.finalY + 15;

    // === VESSEL INFORMATION ===
    y = checkPageBreak(y, 80);
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(11, 62, 101);
    doc.text("Vessel information", m, y);
    y += 10;

    if (report.vessel_data) {
      const vd = report.vessel_data;
      autoTable(doc, {
        startY: y,
        body: [
          ["IMO", vd.imo_number || "N/A", "Class", vd.vessel_type || "N/A"],
          ["MMSI", vd.mmsi || "N/A", "DWT", vd.dwt || "N/A"],
          ["Flag", vd.flag || "N/A", "GRT", vd.gross_tonnage || "N/A"],
          ["Build year", vd.build_year || "N/A", "Build country", vd.build_country || "N/A"],
          ["Call Sign", vd.call_sign || "N/A", "Sanction status", report.vessel_screening?.risk_level === "GREEN" ? "Not sanctioned" : "Potential match"],
        ],
        theme: "plain",
        styles: { fontSize: 9, cellPadding: 4 },
        columnStyles: {
          0: { fontStyle: "bold", cellWidth: 30, textColor: [100, 116, 139] },
          1: { cellWidth: 50 },
          2: { fontStyle: "bold", cellWidth: 30, textColor: [100, 116, 139] },
          3: { cellWidth: 50 },
        },
        margin: { left: m },
      });
      y = doc.lastAutoTable.finalY + 15;
    }

    // === OWNERSHIP STRUCTURE ===
    y = checkPageBreak(y, 60);
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(11, 62, 101);
    doc.text("Estructura de propiedad", m, y);
    y += 10;

    const entities = report.vessel_data?.ownership_entities || [];
    const ownerScreenings = report.ownership_screenings || [];

    if (entities.length > 0) {
      entities.forEach((entity) => {
        y = checkPageBreak(y, 40);
        doc.setFontSize(11);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(51, 51, 51);
        const roleText = entity.since ? `${entity.role} (desde ${entity.since})` : entity.role;
        doc.text(roleText || "Entity", m, y);
        y += 6;
        const match = ownerScreenings.find(s => s.name === entity.name);
        const sanctionText = match ? (match.risk_level === "GREEN" ? "Not Sanctioned" : `Potential Match (${match.risk_level})`) : (entity.sanction_status || "Not checked");
        const addrLabel = entity.address_type === "gestion" ? "Direccion (gestion / C/O)" : "Direccion (registro)";

        const bodyRows = [
          ["Nombre", entity.name || "N/A"],
        ];
        if (entity.imo_company) bodyRows.push(["IMO Corporativo", entity.imo_company]);
        bodyRows.push(["Pais", entity.country || "N/A"]);
        if (entity.address) bodyRows.push([addrLabel, entity.address]);
        bodyRows.push(["Sanciones", sanctionText]);

        autoTable(doc, {
          startY: y,
          body: bodyRows,
          theme: "plain",
          styles: { fontSize: 9, cellPadding: 3 },
          columnStyles: { 0: { fontStyle: "bold", cellWidth: 45, textColor: [100, 116, 139] }, 1: { cellWidth: 115 } },
          margin: { left: m + 5 },
        });
        y = doc.lastAutoTable.finalY + 8;
      });

      // Missing roles notice
      const existingRoles = entities.map(e => e.role.toLowerCase());
      const missingRoles = [];
      if (!existingRoles.some(r => r.includes('beneficial'))) missingRoles.push("Beneficial Owner");
      if (!existingRoles.some(r => r.includes('third') || r.includes('operator'))) missingRoles.push("Third-party Operator");
      if (missingRoles.length > 0) {
        y = checkPageBreak(y, 15);
        doc.setFontSize(8);
        doc.setFont("helvetica", "italic");
        doc.setTextColor(180, 83, 9);
        for (const role of missingRoles) {
          doc.text(`${role}: No disponible en Equasis. Verificar en IHS Markit / Lloyd's List Intelligence.`, m + 5, y);
          y += 4;
        }
        y += 4;
      }
    } else {
      doc.setFontSize(9);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(128, 128, 128);
      doc.text("No ownership data available from Equasis.", m, y);
      y += 10;
    }

    // === REGULATORY RISKS ===
    y = checkPageBreak(y, 50);
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(11, 62, 101);
    doc.text("Regulatory risks", m, y);
    y += 10;

    // Vessel sanctions
    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    doc.text("Vessel", m, y);
    y += 6;
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    if (report.vessel_screening?.risk_level === "GREEN") {
      doc.setTextColor(16, 185, 129);
      doc.text("The vessel has not been listed as sanctioned.", m + 5, y);
    } else {
      doc.setTextColor(239, 68, 68);
      doc.text(report.vessel_screening?.notes || "Potential match found.", m + 5, y);
    }
    y += 8;

    // Ownership sanctions
    doc.setTextColor(51, 51, 51);
    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    doc.text("Ownership", m, y);
    y += 6;

    entities.forEach((entity) => {
      y = checkPageBreak(y, 10);
      const match = ownerScreenings.find(s => s.name === entity.name);
      const isClean = !match || match.risk_level === "GREEN";
      doc.setFontSize(9);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(isClean ? 16 : 239, isClean ? 185 : 68, isClean ? 129 : 68);
      doc.text(`${entity.name} is the vessel's ${entity.role.toLowerCase()} - ${isClean ? "Not Sanctioned" : "Potential Match"}`, m + 5, y);
      y += 5;
    });
    y += 8;

    // Flag
    doc.setTextColor(51, 51, 51);
    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    doc.text("Flag", m, y);
    y += 6;
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    const fr = report.vessel_data?.flag_risk;
    if (fr) {
      doc.setTextColor(fr.flag_list === "BLACK" ? 239 : fr.flag_list === "GREY" ? 245 : 16, fr.flag_list === "BLACK" ? 68 : fr.flag_list === "GREY" ? 158 : 185, fr.flag_list === "BLACK" ? 68 : fr.flag_list === "GREY" ? 11 : 129);
      doc.text(`The vessel's flag (${fr.flag_country}) is on the ${fr.flag_list} List (Paris MoU).`, m + 5, y);
    } else {
      doc.setTextColor(128, 128, 128);
      doc.text("Flag risk data not available.", m + 5, y);
    }
    y += 15;

    // === MANAGEMENT RISKS ===
    y = checkPageBreak(y, 50);
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(11, 62, 101);
    doc.text("Riesgos de gestion", m, y);
    y += 10;

    const vd = report.vessel_data;
    if (vd) {
      const mgmtData = [
        ["Classified", vd.classification_society ? `${vd.classification_society}${vd.class_is_iacs_member ? " (IACS member)" : ""}` : "N/A"],
        ["P&I club", vd.pandi_club ? `${vd.pandi_club}${vd.pandi_is_igp_member ? " (IGP&I member)" : ""}` : "N/A"],
      ];
      autoTable(doc, {
        startY: y, body: mgmtData, theme: "plain",
        styles: { fontSize: 9, cellPadding: 4 },
        columnStyles: { 0: { fontStyle: "bold", cellWidth: 35, textColor: [100, 116, 139] }, 1: { cellWidth: 125 } },
        margin: { left: m },
      });
      y = doc.lastAutoTable.finalY + 10;
    }

    // PSC
    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(51, 51, 51);
    doc.text("Port State Control", m, y);
    y += 6;

    const pscInspections = vd?.psc_inspections || [];
    if (pscInspections.length > 0) {
      autoTable(doc, {
        startY: y,
        head: [["Fecha", "Puerto", "Tipo", "Deficiencias", "Detencion", "Region"]],
        body: pscInspections.map(p => [p.date, `${p.port}${p.country ? ', ' + p.country : ''}`, p.inspection_type, p.deficiencies, p.detention ? "Si" : "No", p.mou_region]),
        theme: "striped",
        headStyles: { fillColor: [11, 62, 101], fontSize: 8 },
        styles: { fontSize: 8, cellPadding: 2 },
        margin: { left: m },
        didParseCell: (data) => {
          if (data.column.index === 4 && data.section === 'body') {
            if (data.cell.raw === "Si") { data.cell.styles.fillColor = [254, 202, 202]; data.cell.styles.textColor = [185, 28, 28]; }
          }
        }
      });
      y = doc.lastAutoTable.finalY + 6;

      // PSC Deficiency breakdown per inspection (last 20 with details)
      const inspWithDetails = pscInspections.filter(p => p.deficiency_details?.length > 0).slice(0, 20);
      if (inspWithDetails.length > 0) {
        y = checkPageBreak(y, 20);
        doc.setFontSize(10);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(51, 51, 51);
        doc.text("Desglose de deficiencias por inspeccion", m, y);
        y += 6;

        for (const insp of inspWithDetails) {
          y = checkPageBreak(y, 15 + insp.deficiency_details.length * 5);
          doc.setFontSize(8);
          doc.setFont("helvetica", "bold");
          doc.setTextColor(11, 62, 101);
          doc.text(`${insp.port}${insp.country ? ', ' + insp.country : ''} — ${insp.date} — ${insp.inspection_type} — ${insp.deficiencies} deficiencia(s)`, m + 2, y);
          y += 4;
          doc.setFont("helvetica", "normal");
          doc.setTextColor(80, 80, 80);
          for (const d of insp.deficiency_details) {
            y = checkPageBreak(y, 5);
            const line = `  - ${d.category}: ${d.deficiency}${d.defect ? ' (' + d.defect + ')' : ''} (${d.total})`;
            const lines = doc.splitTextToSize(line, pw - m * 2 - 10);
            doc.text(lines, m + 5, y);
            y += lines.length * 3.5;
          }
          y += 3;
        }
        y += 4;
      }
    } else {
      doc.setFontSize(9);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(128, 128, 128);
      doc.text("No PSC inspection data available.", m + 5, y);
      y += 10;
    }

    // === FLAG RISKS ===
    y = checkPageBreak(y, 50);
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(11, 62, 101);
    doc.text("Riesgos de bandera", m, y);
    y += 10;

    if (fr) {
      const flagData = [
        ["Flag ranking", `${fr.paris_mou_status || "Not listed"} (Paris MoU) / ${fr.tokyo_mou_status || "Not listed"} (Tokyo MoU)`],
        ["Flag of convenience", fr.flag_of_convenience ? `Si (${fr.foc_source || "ITF"})` : "No"],
        ["USCG Targeted", fr.uscg_targeted ? `Si - ${fr.uscg_ranking || "Targeted"}` : "No"],
        ["IMO conventions", fr.imo_conventions_ratified ? "Ratified" : "Not ratified"],
        ["ILO conventions", fr.ilo_mlc_ratified ? "Ratified" : "Not ratified"],
      ];
      autoTable(doc, {
        startY: y, body: flagData, theme: "plain",
        styles: { fontSize: 9, cellPadding: 4 },
        columnStyles: { 0: { fontStyle: "bold", cellWidth: 40, textColor: [100, 116, 139] }, 1: { cellWidth: 120 } },
        margin: { left: m },
      });
      y = doc.lastAutoTable.finalY + 10;
    }

    // === RISK FACTORS ===
    if (report.risk_factors?.length > 0) {
      y = checkPageBreak(y, 40);
      doc.setFontSize(16);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(11, 62, 101);
      doc.text("Risk factors identified", m, y);
      y += 10;

      autoTable(doc, {
        startY: y,
        head: [["Category", "Factor", "Severity", "Description"]],
        body: report.risk_factors.map(rf => [rf.category, rf.factor, rf.severity, rf.description]),
        theme: "striped",
        headStyles: { fillColor: [243, 112, 34], fontSize: 8 },
        styles: { fontSize: 8, cellPadding: 2 },
        columnStyles: { 0: { cellWidth: 30 }, 1: { cellWidth: 30 }, 2: { cellWidth: 20 }, 3: { cellWidth: 80 } },
        margin: { left: m },
        didParseCell: (data) => {
          if (data.column.index === 2 && data.section === 'body') {
            if (data.cell.raw === "HIGH") { data.cell.styles.fillColor = [254, 202, 202]; data.cell.styles.textColor = [185, 28, 28]; }
            else if (data.cell.raw === "MEDIUM") { data.cell.styles.fillColor = [254, 243, 199]; data.cell.styles.textColor = [180, 83, 9]; }
          }
        }
      });
      y = doc.lastAutoTable.finalY + 10;
    }

    // === RECOMMENDATIONS ===
    if (report.recommendations?.length > 0) {
      y = checkPageBreak(y, 40);
      doc.setFontSize(16);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(11, 62, 101);
      doc.text("Recommendations", m, y);
      y += 10;
      doc.setFontSize(9);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(51, 51, 51);
      report.recommendations.forEach((rec) => {
        y = checkPageBreak(y, 10);
        const lines = doc.splitTextToSize(`- ${rec}`, pw - m * 2);
        doc.text(lines, m, y);
        y += lines.length * 5 + 2;
      });
    }

    // === EXECUTIVE SUMMARY - FINAL VERDICT ===
    if (report.executive_summary) {
      doc.addPage();
      y = 20;
      const es = report.executive_summary;
      const isGo = es.verdict === "GO";
      const isCaution = es.verdict === "CAUTION";

      // Verdict banner
      doc.setFillColor(isGo ? 16 : isCaution ? 245 : 220, isGo ? 185 : isCaution ? 158 : 38, isGo ? 129 : isCaution ? 11 : 38);
      doc.rect(m, y, pw - m * 2, 18, "F");
      doc.setFontSize(16);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(255, 255, 255);
      const verdictLabel = isGo ? "GO - Operacion aprobada" : isCaution ? "CAUTION - Proceder con precaucion" : "NO GO - Operacion no recomendada";
      doc.text(verdictLabel, pw / 2, y + 11, { align: "center" });
      y += 25;

      // Subtitle
      doc.setFontSize(12);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(11, 62, 101);
      doc.text("Informe de recomendacion para Green Bunkers", m, y);
      y += 10;

      // Verdict text
      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(51, 51, 51);
      const verdictLines = doc.splitTextToSize(es.verdict_text || "", pw - m * 2);
      doc.text(verdictLines, m, y);
      y += verdictLines.length * 5 + 8;

      // Positive points
      if (es.positive_points?.length > 0) {
        y = checkPageBreak(y, 20);
        doc.setFontSize(11);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(16, 185, 129);
        doc.text("Puntos positivos", m, y);
        y += 7;
        doc.setFontSize(9);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(51, 51, 51);
        es.positive_points.forEach(point => {
          y = checkPageBreak(y, 12);
          doc.setFillColor(236, 253, 245);
          const pointLines = doc.splitTextToSize(point, pw - m * 2 - 10);
          doc.rect(m, y - 3, pw - m * 2, pointLines.length * 5 + 4, "F");
          doc.text(pointLines, m + 5, y);
          y += pointLines.length * 5 + 6;
        });
        y += 4;
      }

      // Concerns
      if (es.concerns?.length > 0) {
        y = checkPageBreak(y, 20);
        doc.setFontSize(11);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(180, 83, 9);
        doc.text("Alertas identificadas", m, y);
        y += 8;

        es.concerns.forEach(concern => {
          y = checkPageBreak(y, 35);
          const isHigh = concern.severity === "HIGH";
          const isMed = concern.severity === "MEDIUM";

          // Left border color
          doc.setFillColor(isHigh ? 239 : isMed ? 245 : 203, isHigh ? 68 : isMed ? 158 : 213, isHigh ? 68 : isMed ? 11 : 225);
          doc.rect(m, y - 3, 3, 18, "F");

          // Background
          doc.setFillColor(isHigh ? 254 : isMed ? 255 : 248, isHigh ? 242 : isMed ? 251 : 250, isHigh ? 242 : isMed ? 235 : 252);
          doc.rect(m + 3, y - 3, pw - m * 2 - 3, 18, "F");

          // Severity badge
          doc.setFontSize(7);
          doc.setFont("helvetica", "bold");
          doc.setTextColor(isHigh ? 185 : isMed ? 180 : 100, isHigh ? 28 : isMed ? 83 : 116, isHigh ? 28 : isMed ? 9 : 139);
          const severityText = isHigh ? "ALTO" : isMed ? "MEDIO" : "BAJO";
          doc.text(severityText, pw - m - 2, y + 1, { align: "right" });

          // Title
          doc.setFontSize(9);
          doc.setFont("helvetica", "bold");
          doc.setTextColor(51, 51, 51);
          doc.text(concern.title, m + 7, y + 1);

          // Detail
          doc.setFontSize(8);
          doc.setFont("helvetica", "normal");
          doc.setTextColor(100, 116, 139);
          const detailLines = doc.splitTextToSize(concern.detail, pw - m * 2 - 15);
          doc.text(detailLines, m + 7, y + 7);
          y += Math.max(18, detailLines.length * 4 + 10) + 2;

          // Verification sources
          if (concern.verification_sources?.length > 0) {
            y = checkPageBreak(y, 12);
            doc.setFontSize(7);
            doc.setFont("helvetica", "bold");
            doc.setTextColor(100, 116, 139);
            doc.text("Verificar en:", m + 7, y);
            y += 4;
            doc.setFont("helvetica", "normal");
            doc.setTextColor(11, 62, 101);
            concern.verification_sources.forEach(src => {
              y = checkPageBreak(y, 5);
              doc.textWithLink(src.name, m + 12, y, { url: src.url });
              y += 4;
            });
          }
          if (concern.verification_tip) {
            y = checkPageBreak(y, 8);
            doc.setFontSize(7);
            doc.setFont("helvetica", "italic");
            doc.setTextColor(148, 163, 184);
            const tipLines = doc.splitTextToSize(concern.verification_tip, pw - m * 2 - 20);
            doc.text(tipLines, m + 7, y);
            y += tipLines.length * 3.5;
          }
          y += 4;
        });
      }

      // Sources note
      y = checkPageBreak(y, 15);
      doc.setFontSize(8);
      doc.setFont("helvetica", "italic");
      doc.setTextColor(148, 163, 184);
      doc.text(`Basado en ${es.sources_checked || 0} de ${es.sources_total || 0} fuentes de sanciones consultadas. Screening alineado con OFAC 2025 Maritime Advisory.`, m, y);
    }

    // === DATA SOURCES ===
    y = checkPageBreak(y, 30);

    // Coverage Score in PDF
    if (report.executive_summary?.coverage_score != null) {
      doc.setFontSize(12);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(11, 62, 101);
      doc.text("Cobertura del screening", m, y);
      y += 8;
      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(51, 51, 51);
      doc.text(`Puntuacion de cobertura: ${report.executive_summary.coverage_score}%`, m, y);
      y += 6;
      if (report.executive_summary.missing_areas?.length > 0) {
        doc.setFontSize(8);
        doc.setTextColor(180, 83, 9);
        doc.text("Areas sin cobertura (requieren herramientas de pago):", m, y);
        y += 5;
        report.executive_summary.missing_areas.forEach(area => {
          y = checkPageBreak(y, 6);
          doc.text(`  - ${area}`, m, y);
          y += 4;
        });
      }
      y += 8;
    }

    // Fuentes no cubiertas in PDF
    if (report.executive_summary?.sources_not_covered?.length > 0) {
      y = checkPageBreak(y, 40);
      doc.setFontSize(12);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(180, 83, 9);
      doc.text("Fuentes no cubiertas", m, y);
      y += 8;
      doc.setFontSize(8);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(51, 51, 51);
      report.executive_summary.sources_not_covered.forEach(src => {
        y = checkPageBreak(y, 15);
        doc.setFont("helvetica", "bold");
        doc.text(`${src.area} [Impacto: ${src.risk_impact}]`, m, y);
        y += 4;
        doc.setFont("helvetica", "normal");
        const descLines = doc.splitTextToSize(src.description, pw - m * 2 - 5);
        doc.text(descLines, m + 3, y);
        y += descLines.length * 4 + 2;
        doc.setTextColor(100, 116, 139);
        doc.text(`Herramientas: ${src.required_tools}`, m + 3, y);
        doc.setTextColor(51, 51, 51);
        y += 6;
      });
      y += 5;
    }

    y = checkPageBreak(y, 30);
    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(11, 62, 101);
    doc.text("Data sources consulted", m, y);
    y += 8;
    doc.setFontSize(8);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(100, 116, 139);
    (report.data_sources_consulted || []).forEach(src => {
      y = checkPageBreak(y, 8);
      doc.text(`- ${src}`, m, y);
      y += 5;
    });

    addFooter();

    const name = report.vessel_data?.vessel_name || "Report";
    const imo = report.vessel_data?.imo_number || "";
    doc.save(`Compliance_${name.replace(/\s+/g, '_')}_${imo}_${new Date().toISOString().split("T")[0]}.pdf`);
    toast.success("PDF exported");
  };

  const handleExportExcel = async () => {
    try {
      const res = await axios.get(`${API}/reports/${id}/export/excel`, { responseType: "blob" });
      const name = report.vessel_data?.vessel_name || "Report";
      const imo = report.vessel_data?.imo_number || "";
      saveAs(res.data, `Compliance_${name.replace(/\s+/g, '_')}_${imo}_${new Date().toISOString().split("T")[0]}.xlsx`);
      toast.success("Excel exported");
    } catch (error) {
      toast.error("Failed to export Excel");
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="w-8 h-8 animate-spin text-[#0b3e65]" />
        </div>
      </Layout>
    );
  }

  if (!report) {
    return (
      <Layout>
        <div className="text-center py-12">
          <p className="text-slate-500">Report not found</p>
          <Link to="/reports"><Button className="mt-4">Back to Reports</Button></Link>
        </div>
      </Layout>
    );
  }

  const vd = report.vessel_data;
  const vs = report.vessel_screening;
  const flagRisk = vd?.flag_risk;
  const ownerEntities = vd?.ownership_entities || [];
  const ownerScreenings = report.ownership_screenings || [];
  const pscData = vd?.psc_inspections || [];

  return (
    <Layout>
      <div className="space-y-6" data-testid="report-view-page">
        {/* Action Bar */}
        <div className="flex items-center justify-between no-print">
          <Link to="/reports">
            <Button variant="ghost" className="text-slate-600" data-testid="btn-back">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back to Reports
            </Button>
          </Link>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => window.print()} className="rounded-sm" data-testid="btn-print">
              <Printer className="w-4 h-4 mr-2" /> Print
            </Button>
            <Button variant="outline" onClick={handleExportExcel} className="rounded-sm" data-testid="btn-export-excel">
              <Download className="w-4 h-4 mr-2" /> Excel
            </Button>
            <Button onClick={handleExportPDF} className="bg-[#0b3e65] hover:bg-[#0b3e65]/90 rounded-sm" data-testid="btn-export-pdf">
              <Download className="w-4 h-4 mr-2" /> PDF
            </Button>
          </div>
        </div>

        {/* Report Document */}
        <div className="bg-white shadow-lg border border-slate-200 max-w-4xl mx-auto print:shadow-none print:border-none">
          {/* TITLE HEADER */}
          <div className="bg-[#0b3e65] text-white p-8">
            <div className="flex justify-between items-start">
              <div>
                <h1 className="text-3xl font-bold tracking-tight">{vd?.vessel_name || "Vessel"}</h1>
                <p className="text-slate-300 mt-1 text-sm">Risk & compliance report</p>
              </div>
              <div className="text-right">
                <img
                  src="https://customer-assets.emergentagent.com/job_fae3f46c-8b67-432c-9c77-692e590edf8a/artifacts/1nzgjyki_GREEN%20BUNKERS%20negarivo.png"
                  alt="Green Bunkers"
                  className="h-14 w-auto mb-2 ml-auto"
                />
                <p className="text-sm text-slate-300">{formatDate(report.created_at)}</p>
              </div>
            </div>
          </div>

          {/* Risk Banner */}
          <div className={`p-3 flex items-center justify-center gap-2 text-white font-semibold ${
            report.executive_summary?.verdict === "INCOMPLETE" ? "bg-slate-600" :
            report.overall_risk === "GREEN" ? "bg-emerald-500" :
            report.overall_risk === "YELLOW" ? "bg-amber-500" :
            report.overall_risk === "RED" ? "bg-red-500" : "bg-slate-500"
          }`}>
            {report.executive_summary?.verdict === "INCOMPLETE" ? <AlertTriangle className="w-5 h-5" /> :
             report.overall_risk === "GREEN" ? <CheckCircle className="w-5 h-5" /> :
             report.overall_risk === "YELLOW" ? <AlertTriangle className="w-5 h-5" /> :
             <XCircle className="w-5 h-5" />}
            {report.executive_summary?.verdict === "INCOMPLETE"
              ? "EVALUACION INCOMPLETA - SE REQUIERE SCREENING COMERCIAL"
              : report.overall_risk === "GREEN" ? "LOW RISK - COMPLIANT"
              : report.overall_risk === "YELLOW" ? "MEDIUM RISK - INVESTIGATION REQUIRED"
              : "HIGH RISK - DO NOT PROCEED"}
          </div>

          {/* Coverage Score Bar */}
          {report.executive_summary?.coverage_score != null && (
            <div className="px-8 py-4 bg-slate-50 border-b border-slate-200" data-testid="coverage-score-section">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Cobertura del screening</span>
                <span className={`text-sm font-bold ${
                  report.executive_summary.coverage_score >= 70 ? "text-emerald-600" :
                  report.executive_summary.coverage_score >= 50 ? "text-amber-600" : "text-red-600"
                }`}>
                  {report.executive_summary.coverage_score}%
                </span>
              </div>
              <div className="w-full bg-slate-200 rounded-full h-2.5">
                <div
                  className={`h-2.5 rounded-full transition-all ${
                    report.executive_summary.coverage_score >= 70 ? "bg-emerald-500" :
                    report.executive_summary.coverage_score >= 50 ? "bg-amber-500" : "bg-red-500"
                  }`}
                  style={{ width: `${report.executive_summary.coverage_score}%` }}
                />
              </div>
              <div className="flex justify-between mt-2">
                <span className="text-xs text-slate-400">
                  {report.executive_summary.checked_areas?.length || 0} areas verificadas
                </span>
                <span className="text-xs text-amber-500 font-medium">
                  {report.executive_summary.missing_areas?.length || 0} areas sin cobertura (requieren herramientas de pago)
                </span>
              </div>
            </div>
          )}

          <div className="p-8 space-y-10">
            {/* 1. GENERAL INFORMATION */}
            <section>
              <SectionHeader number={1} title="Informacion general del buque" icon={Info} />
              <div className="bg-slate-50 rounded-sm p-5 space-y-0">
                <DataRow label="Vessel" value={vd?.vessel_name} />
                <DataRow label="Sanction status" value={
                  vs?.risk_level === "GREEN" ? "Not a sanctioned vessel" :
                  vs?.risk_level === "RED" ? "Potential sanctions match found" :
                  "Under investigation"
                } />
                <DataRow label="Investigation timeframe" value={report.investigation_timeframe || "24 months"} />
                <DataRow label="Report created by" value={report.created_by_email || user?.email} />
                <DataRow label="Report date" value={formatDateTime(report.created_at)} />
                <DataRow label="Report ID" value={report.id?.substring(0, 8).toUpperCase()} />
              </div>
            </section>

            {/* 2. VESSEL INFORMATION */}
            {vd && (
              <section>
                <SectionHeader number={2} title="Identificacion del buque" icon={Ship} />
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {[
                    { label: "IMO", value: vd.imo_number },
                    { label: "Tipo", value: vd.vessel_type },
                    { label: "Pais de construccion", value: vd.build_country ? `${vd.build_country} *` : "N/A" },
                    { label: "MMSI", value: vd.mmsi },
                    { label: "DWT", value: vd.dwt },
                    { label: "Ano de construccion", value: vd.build_year },
                    { label: "Bandera", value: vd.flag },
                    { label: "GRT", value: vd.gross_tonnage },
                    { label: "Call Sign", value: vd.call_sign },
                  ].map((item, idx) => (
                    <div key={idx} className="p-3 bg-slate-50 rounded-sm">
                      <p className="text-xs text-slate-400 uppercase tracking-wider">{item.label}</p>
                      <p className="font-medium text-slate-800 mt-1">{item.value || "N/A"}</p>
                    </div>
                  ))}
                </div>
                {vd.build_country && (
                  <p className="text-xs text-slate-400 mt-2 italic">
                    * Pais de construccion inferido de la sociedad de clasificacion ({vd.classification_society}). Verificar en IHS Markit para confirmacion.
                  </p>
                )}
                <div className="mt-4 p-3 bg-slate-50 rounded-sm">
                  <DataRow label="Estado de sanciones" value={
                    vs?.risk_level === "GREEN" ? "El buque no aparece en listas de sanciones" :
                    "Posible coincidencia - requiere investigacion"
                  } />
                </div>
              </section>
            )}

            {/* 3. OWNERSHIP STRUCTURE */}
            <section>
              <SectionHeader number={3} title="Estructura de propiedad" icon={Building2} />
              {ownerEntities.length > 0 ? (
                <div className="space-y-5">
                  {ownerEntities.map((entity, idx) => {
                    const screening = ownerScreenings.find(s => s.name === entity.name);
                    const isClean = !screening || screening.risk_level === "GREEN";
                    return (
                      <div key={idx} className="border border-slate-200 rounded-sm p-4">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <h4 className="text-sm font-semibold text-[#0b3e65] uppercase tracking-wide">{entity.role}</h4>
                            {entity.since && (
                              <span className="text-xs text-slate-400 font-normal normal-case">desde {entity.since}</span>
                            )}
                          </div>
                          <StatusBadge status={isClean ? "Not Sanctioned" : `Potential Match`} isPositive={isClean} />
                        </div>
                        <div className="space-y-0">
                          <DataRow label="Nombre" value={entity.name} />
                          {entity.imo_company && <DataRow label="IMO Corporativo" value={entity.imo_company} />}
                          <DataRow label="Pais" value={entity.country} />
                          {entity.address && (
                            <DataRow
                              label={entity.address_type === "gestion" ? "Direccion (gestion / C/O)" : "Direccion (registro)"}
                              value={entity.address}
                            />
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-sm text-slate-400 italic">No ownership data available from Equasis.</p>
              )}

              {/* Missing roles notice */}
              {ownerEntities.length > 0 && (() => {
                const roles = ownerEntities.map(e => e.role.toLowerCase());
                const missing = [];
                if (!roles.some(r => r.includes('beneficial'))) missing.push({ role: "Beneficial Owner", note: "No disponible en Equasis. Verificar en IHS Markit / Lloyd's List Intelligence." });
                if (!roles.some(r => r.includes('third') || r.includes('operator'))) missing.push({ role: "Third-party Operator", note: "No disponible en Equasis." });
                if (!roles.some(r => r.includes('technical') && !r.includes('commercial'))) missing.push({ role: "Technical Manager", note: "No diferenciado de Ship Manager en Equasis. Puede coincidir con el Ship manager/Commercial manager." });
                return missing.length > 0 ? (
                  <div className="mt-4 space-y-2" data-testid="missing-ownership-roles">
                    {missing.map((m, i) => (
                      <div key={i} className="flex items-start gap-2 p-2 bg-amber-50 border border-amber-100 rounded-sm">
                        <AlertTriangle className="w-3.5 h-3.5 text-amber-500 flex-shrink-0 mt-0.5" />
                        <span className="text-xs text-amber-700">
                          <strong>{m.role}:</strong> {m.note}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : null;
              })()}
            </section>

            {/* 4. REGULATORY RISKS */}
            <section>
              <SectionHeader number={4} title="Screening de sanciones" icon={Shield} />

              {/* Vessel */}
              <div className="mb-6">
                <h4 className="text-sm font-semibold text-slate-700 mb-2">Vessel</h4>
                {vs?.risk_level === "GREEN" ? (
                  <div className="flex items-center gap-2 text-emerald-600 text-sm p-3 bg-emerald-50 rounded-sm">
                    <CheckCircle className="w-4 h-4" />
                    The vessel has not been listed as sanctioned.
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-red-600 text-sm p-3 bg-red-50 rounded-sm">
                    <XCircle className="w-4 h-4" />
                    {vs?.notes || "Potential match found"}
                  </div>
                )}
              </div>

              {/* Sanctions matches detail */}
              {vs?.sanctions_matches?.length > 0 && (
                <div className="mb-6 bg-red-50 border border-red-200 rounded-sm p-4">
                  <h4 className="text-sm font-semibold text-red-700 mb-3 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" /> Vessel Sanctions Matches ({vs.sanctions_matches.length})
                  </h4>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-red-700">Source</TableHead>
                        <TableHead className="text-red-700">Entity</TableHead>
                        <TableHead className="text-red-700">Score</TableHead>
                        <TableHead className="text-red-700">Program</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {vs.sanctions_matches.map((m, idx) => (
                        <TableRow key={idx}>
                          <TableCell className="text-sm">{m.source}</TableCell>
                          <TableCell className="text-sm">{m.entity_name}</TableCell>
                          <TableCell className="text-sm font-mono font-bold text-red-600">{m.match_score?.toFixed(1)}%</TableCell>
                          <TableCell className="text-sm">{m.program || "N/A"}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}

              {/* Ownership sanctions */}
              <div className="mb-6">
                <h4 className="text-sm font-semibold text-slate-700 mb-2">Ownership</h4>
                <div className="space-y-2">
                  {ownerEntities.map((entity, idx) => {
                    const screening = ownerScreenings.find(s => s.name === entity.name);
                    const isClean = !screening || screening.risk_level === "GREEN";
                    return (
                      <div key={idx} className={`flex items-center gap-2 text-sm p-2 rounded-sm ${isClean ? "text-emerald-700 bg-emerald-50" : "text-red-700 bg-red-50"}`}>
                        {isClean ? <CheckCircle className="w-3.5 h-3.5 flex-shrink-0" /> : <XCircle className="w-3.5 h-3.5 flex-shrink-0" />}
                        <span><strong>{entity.name}</strong> is the vessel's {entity.role.toLowerCase()} - {isClean ? "Not Sanctioned" : "Potential Match"}</span>
                      </div>
                    );
                  })}
                  {ownerEntities.length === 0 && (
                    <p className="text-sm text-slate-400 italic">No ownership data available.</p>
                  )}
                </div>
              </div>

              {/* Flag */}
              <div>
                <h4 className="text-sm font-semibold text-slate-700 mb-2">Flag</h4>
                {flagRisk ? (
                  <div className={`flex items-center gap-2 text-sm p-3 rounded-sm ${
                    flagRisk.flag_list === "BLACK" ? "text-red-700 bg-red-50" :
                    flagRisk.flag_list === "GREY" ? "text-amber-700 bg-amber-50" :
                    "text-emerald-700 bg-emerald-50"
                  }`}>
                    <Flag className="w-4 h-4 flex-shrink-0" />
                    The vessel's flag ({flagRisk.flag_country}) is on the {flagRisk.flag_list} List (Paris MoU).
                    {flagRisk.flag_of_convenience && " Flag of Convenience (ITF)."}
                  </div>
                ) : (
                  <p className="text-sm text-slate-400 italic">Flag risk data not available.</p>
                )}
              </div>
            </section>

            {/* 5. MANAGEMENT RISKS */}
            <section>
              <SectionHeader number={5} title="Riesgos de gestion" icon={Award} />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div className="border border-slate-200 rounded-sm p-4">
                  <p className="text-xs text-slate-400 uppercase tracking-wider mb-1">Classification Society</p>
                  <p className="font-medium">{vd?.classification_society || "N/A"}</p>
                  {vd?.classification_society && (
                    <StatusBadge
                      status={vd.class_is_iacs_member ? "IACS member" : "Not IACS member"}
                      isPositive={vd.class_is_iacs_member}
                    />
                  )}
                </div>
                <div className="border border-slate-200 rounded-sm p-4">
                  <p className="text-xs text-slate-400 uppercase tracking-wider mb-1">P&I Club</p>
                  <p className="font-medium">{vd?.pandi_club || "N/A"}</p>
                  {vd?.pandi_club && (
                    <StatusBadge
                      status={vd.pandi_is_igp_member ? "IGP&I member" : "Not IGP&I member"}
                      isPositive={vd.pandi_is_igp_member}
                    />
                  )}
                </div>
              </div>

              {/* PSC Inspections */}
              <div>
                <h4 className="text-sm font-semibold text-slate-700 mb-3">Port State Control</h4>
                {pscData.length > 0 ? (
                  <>
                    {/* PSC Summary Stats */}
                    <div className="grid grid-cols-3 gap-3 mb-4">
                      <div className="p-3 bg-slate-50 rounded-sm border border-slate-200 text-center">
                        <p className="text-xs text-slate-400 uppercase">Inspecciones</p>
                        <p className="text-lg font-bold text-slate-800">{pscData.length}</p>
                      </div>
                      <div className="p-3 bg-slate-50 rounded-sm border border-slate-200 text-center">
                        <p className="text-xs text-slate-400 uppercase">Deficiencias totales</p>
                        <p className="text-lg font-bold text-slate-800">{pscData.reduce((sum, p) => sum + (p.deficiencies || 0), 0)}</p>
                      </div>
                      <div className="p-3 bg-slate-50 rounded-sm border border-slate-200 text-center">
                        <p className="text-xs text-slate-400 uppercase">Detenciones</p>
                        <p className={`text-lg font-bold ${pscData.some(p => p.detention) ? "text-red-600" : "text-emerald-600"}`}>
                          {pscData.filter(p => p.detention).length}
                        </p>
                      </div>
                    </div>
                    <Table data-testid="psc-inspections-table">
                      <TableHeader>
                        <TableRow>
                          <TableHead>Fecha</TableHead>
                          <TableHead>Puerto</TableHead>
                          <TableHead>Tipo</TableHead>
                          <TableHead>Deficiencias</TableHead>
                          <TableHead>Detencion</TableHead>
                          <TableHead>Region</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {pscData.map((psc, idx) => (
                          <React.Fragment key={idx}>
                            <TableRow className={psc.detention ? "bg-red-50" : ""}>
                              <TableCell className="text-sm">{psc.date}</TableCell>
                              <TableCell className="text-sm">{psc.port}{psc.country ? `, ${psc.country}` : ""}</TableCell>
                              <TableCell className="text-sm">{psc.inspection_type}</TableCell>
                              <TableCell className="text-sm font-medium">{psc.deficiencies}</TableCell>
                              <TableCell>
                                {psc.detention ? (
                                  <span className="text-xs px-2 py-0.5 rounded bg-red-100 text-red-700 font-medium">Si</span>
                                ) : (
                                  <span className="text-xs px-2 py-0.5 rounded bg-emerald-100 text-emerald-700">No</span>
                                )}
                              </TableCell>
                              <TableCell className="text-sm">{psc.mou_region}</TableCell>
                            </TableRow>
                            {/* Deficiency breakdown */}
                            {psc.deficiency_details?.length > 0 && (
                              <TableRow>
                                <TableCell colSpan={6} className="bg-slate-50 px-8 py-2">
                                  <div className="text-xs space-y-1">
                                    <p className="font-semibold text-slate-600 mb-1">Desglose de deficiencias:</p>
                                    {psc.deficiency_details.map((d, di) => (
                                      <div key={di} className="flex items-start gap-2 text-slate-500">
                                        <span className="text-slate-400">-</span>
                                        <span>
                                          <span className="font-medium text-slate-600">{d.category}</span>
                                          {d.deficiency && `: ${d.deficiency}`}
                                          {d.defect && ` (${d.defect})`}
                                          <span className="text-slate-400 ml-1">({d.total})</span>
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                </TableCell>
                              </TableRow>
                            )}
                          </React.Fragment>
                        ))}
                      </TableBody>
                    </Table>
                    <p className="text-xs text-slate-400 mt-2 italic">
                      Fuente: Equasis (EMSA). Datos de inspecciones PSC disponibles publicamente.
                    </p>
                  </>
                ) : (
                  <div className="bg-slate-50 rounded-sm p-4 space-y-3">
                    {(vd?.psc_deficiencies_total > 0 || vd?.psc_detentions_total >= 0) ? (
                      <div>
                        <p className="text-sm text-slate-600 mb-3">
                          PSC Resumen (fuente: Equasis)
                        </p>
                        <div className="grid grid-cols-2 gap-3">
                          <div className="p-3 bg-white rounded-sm border border-slate-200 text-center">
                            <p className="text-xs text-slate-400 uppercase">Total inspecciones</p>
                            <p className="text-lg font-bold text-slate-800">{vd?.psc_deficiencies_total || 0}</p>
                          </div>
                          <div className="p-3 bg-white rounded-sm border border-slate-200 text-center">
                            <p className="text-xs text-slate-400 uppercase">Detenciones (36m)</p>
                            <p className={`text-lg font-bold ${vd?.psc_detentions_total > 0 ? "text-red-600" : "text-emerald-600"}`}>
                              {vd?.psc_detentions_total || 0}
                            </p>
                          </div>
                        </div>
                        <p className="text-xs text-amber-500 mt-3 font-medium">
                          No se obtuvieron inspecciones PSC individuales. Verificar manualmente en{" "}
                          <a href={`https://www.equasis.org/EquasisWeb/restricted/ShipInfo?fs=Inspection&P_IMO=${vd?.imo_number}`} target="_blank" rel="noopener noreferrer" className="text-[#0b3e65] underline">Equasis</a>
                        </p>
                      </div>
                    ) : (
                      <p className="text-sm text-slate-400 text-center">
                        No hay datos de inspecciones PSC disponibles.
                      </p>
                    )}
                  </div>
                )}
              </div>
            </section>

            {/* 6. OPERATIONAL RISKS */}
            <section>
              <SectionHeader number={6} title="Port State Control" icon={Anchor} />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  { label: "AIS gaps", count: vd?.ais_gaps?.length || 0, desc: vd?.ais_gaps?.length ? `${vd.ais_gaps.length} gap(s) detected` : "Sin datos - requiere MarineTraffic/Kpler API" },
                  { label: "High risk STS transfers", count: 0, desc: "Sin datos - requiere MarineTraffic/Kpler API" },
                  { label: "Dark STS transfers", count: 0, desc: "Sin datos - requiere MarineTraffic/Kpler API" },
                  { label: "AIS spoofing", count: 0, desc: "Sin datos - requiere MarineTraffic/Kpler API" },
                ].map((item, idx) => (
                  <div key={idx} className={`p-4 rounded-sm border ${
                    item.count > 0 ? "bg-amber-50 border-amber-200" :
                    vd?.ais_gaps?.length === undefined && item.label === "AIS gaps" ? "bg-slate-50 border-slate-200" :
                    "bg-slate-50 border-slate-200"
                  }`}>
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-sm font-medium text-slate-700">{item.label}</p>
                      <span className={`text-xs font-bold px-2 py-0.5 rounded ${item.count > 0 ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}>
                        {item.count}
                      </span>
                    </div>
                    <p className={`text-xs ${item.count > 0 ? "text-amber-600" : "text-emerald-600"}`}>{item.desc}</p>
                  </div>
                ))}
              </div>

              {/* AIS Gaps Table */}
              {vd?.ais_gaps?.length > 0 && (
                <div className="mt-4">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>From</TableHead>
                        <TableHead>To</TableHead>
                        <TableHead>Duration</TableHead>
                        <TableHead>Zone</TableHead>
                        <TableHead>Risk</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {vd.ais_gaps.map((gap, idx) => (
                        <TableRow key={idx}>
                          <TableCell className="text-sm">{gap.date_from}</TableCell>
                          <TableCell className="text-sm">{gap.date_to}</TableCell>
                          <TableCell className="text-sm">{gap.duration}</TableCell>
                          <TableCell className="text-sm">{gap.zone_start} - {gap.zone_end}</TableCell>
                          <TableCell><span className="text-xs px-2 py-0.5 rounded bg-amber-100 text-amber-700">{gap.risk}</span></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </section>

            {/* 7. FLAG RISKS */}
            <section>
              <SectionHeader number={7} title="Riesgos de bandera" icon={Flag} />

              {flagRisk ? (
                <div className="space-y-4">
                  {/* Flag ranking */}
                  <div className={`p-4 rounded-sm border ${
                    flagRisk.flag_list === "BLACK" ? "bg-red-50 border-red-200" :
                    flagRisk.flag_list === "GREY" ? "bg-amber-50 border-amber-200" :
                    "bg-emerald-50 border-emerald-200"
                  }`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-slate-700">Flag ranking</p>
                        <p className="text-xs text-slate-500 mt-1">Paris MoU & Tokyo MoU</p>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                        flagRisk.flag_list === "BLACK" ? "bg-red-100 text-red-700" :
                        flagRisk.flag_list === "GREY" ? "bg-amber-100 text-amber-700" :
                        "bg-emerald-100 text-emerald-700"
                      }`}>{flagRisk.flag_list} LIST</span>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="p-4 border border-slate-200 rounded-sm">
                      <p className="text-xs text-slate-400 uppercase mb-1">Bandera de conveniencia (FoC)</p>
                      <p className="font-medium">{flagRisk.flag_of_convenience ? "Si" : "No"}</p>
                      {flagRisk.flag_of_convenience && (
                        <p className="text-xs text-slate-400 mt-1">Fuente: {flagRisk.foc_source || "ITF"}</p>
                      )}
                    </div>
                    <div className={`p-4 border rounded-sm ${flagRisk.uscg_targeted ? "border-amber-300 bg-amber-50" : "border-slate-200"}`}>
                      <p className="text-xs text-slate-400 uppercase mb-1">USCG Targeted Flag</p>
                      <p className={`font-medium ${flagRisk.uscg_targeted ? "text-amber-700" : "text-slate-800"}`}>
                        {flagRisk.uscg_targeted ? `Si - ${flagRisk.uscg_ranking || "Targeted"}` : "No"}
                      </p>
                      {flagRisk.uscg_targeted && (
                        <p className="text-xs text-amber-600 mt-1">
                          Los buques con esta bandera seran sujetos a inspecciones mas frecuentes al entrar en puertos de EE.UU.
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex items-center gap-2 p-3 bg-emerald-50 rounded-sm border border-emerald-200 text-sm text-emerald-700">
                      <CheckCircle className="w-4 h-4" />
                      IMO conventions: {flagRisk.imo_conventions_ratified ? "Ratified" : "Not ratified"}
                    </div>
                    <div className="flex items-center gap-2 p-3 bg-emerald-50 rounded-sm border border-emerald-200 text-sm text-emerald-700">
                      <CheckCircle className="w-4 h-4" />
                      ILO MLC: {flagRisk.ilo_mlc_ratified ? "Ratified" : "Not ratified"}
                    </div>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-slate-400 italic">Flag risk data not available.</p>
              )}
            </section>

            {/* 8. RISK FACTORS */}
            {report.risk_factors?.length > 0 && (
              <section>
                <SectionHeader number={8} title="Alertas identificadas" icon={AlertTriangle} />
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Category</TableHead>
                      <TableHead>Factor</TableHead>
                      <TableHead>Severity</TableHead>
                      <TableHead>Description</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {report.risk_factors.map((rf, idx) => (
                      <TableRow key={idx}>
                        <TableCell className="text-sm font-medium">{rf.category}</TableCell>
                        <TableCell className="text-sm">{rf.factor}</TableCell>
                        <TableCell>
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                            rf.severity === "HIGH" ? "bg-red-100 text-red-700" :
                            rf.severity === "MEDIUM" ? "bg-amber-100 text-amber-700" :
                            "bg-slate-100 text-slate-700"
                          }`}>{rf.severity}</span>
                        </TableCell>
                        <TableCell className="text-sm">{rf.description}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </section>
            )}

            {/* 9. RECOMMENDATIONS */}
            {report.recommendations?.length > 0 && (
              <section>
                <SectionHeader number={report.risk_factors?.length > 0 ? 9 : 8} title="Recomendacion" icon={FileText} />
                <ul className="space-y-2">
                  {report.recommendations.map((rec, idx) => {
                    const isRed = rec.startsWith("RED FLAG") || rec.startsWith("STOP");
                    const isYellow = rec.startsWith("INVESTIGATE");
                    return (
                      <li key={idx} className={`flex items-start gap-3 p-3 rounded-sm ${
                        isRed ? "bg-red-50 border border-red-200" :
                        isYellow ? "bg-amber-50 border border-amber-200" :
                        "bg-slate-50"
                      }`}>
                        <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                          isRed ? "bg-red-100 text-red-700" :
                          isYellow ? "bg-amber-100 text-amber-700" :
                          "bg-emerald-100 text-emerald-700"
                        }`}>{idx + 1}</span>
                        <span className="text-sm text-slate-700">{rec}</span>
                      </li>
                    );
                  })}
                </ul>
              </section>
            )}

            {/* EXECUTIVE SUMMARY - FINAL VERDICT */}
            {report.executive_summary && (
              <section>
                <div className={`border-2 rounded-sm overflow-hidden ${
                  report.executive_summary.verdict === "GO" ? "border-emerald-400" :
                  report.executive_summary.verdict === "CAUTION" ? "border-amber-400" :
                  "border-red-400"
                }`}>
                  {/* Verdict Header */}
                  <div className={`p-5 text-white ${
                    report.executive_summary.verdict === "GO" ? "bg-emerald-600" :
                    report.executive_summary.verdict === "CAUTION" ? "bg-amber-500" :
                    "bg-red-600"
                  }`}>
                    <div className="flex items-center gap-3">
                      {report.executive_summary.verdict === "GO" ? <CheckCircle className="w-7 h-7" /> :
                       report.executive_summary.verdict === "CAUTION" ? <AlertTriangle className="w-7 h-7" /> :
                       <XCircle className="w-7 h-7" />}
                      <div>
                        <h2 className="text-xl font-bold tracking-wide">
                          {report.executive_summary.verdict === "GO" ? "GO - Operacion aprobada" :
                           report.executive_summary.verdict === "CAUTION" ? "CAUTION - Proceder con precaucion" :
                           "NO GO - Operacion no recomendada"}
                        </h2>
                        <p className="text-sm opacity-90 mt-1">Informe de recomendacion para Green Bunkers</p>
                      </div>
                    </div>
                  </div>

                  {/* Verdict Body */}
                  <div className="p-6 space-y-5">
                    {/* Summary Text */}
                    <div className={`p-4 rounded-sm text-sm leading-relaxed ${
                      report.executive_summary.verdict === "GO" ? "bg-emerald-50 text-emerald-900 border border-emerald-200" :
                      report.executive_summary.verdict === "CAUTION" ? "bg-amber-50 text-amber-900 border border-amber-200" :
                      "bg-red-50 text-red-900 border border-red-200"
                    }`}>
                      {report.executive_summary.verdict_text?.split('. ').filter(Boolean).map((sentence, idx) => {
                        const s = sentence.endsWith('.') ? sentence : sentence + '.';
                        const isBullet = s.startsWith('- ');
                        if (isBullet) {
                          return <p key={idx} className="ml-4 mt-1">{s}</p>;
                        }
                        return <p key={idx} className={idx > 0 ? "mt-2" : ""}>{s}</p>;
                      })}
                    </div>

                    {/* Positive Points */}
                    {report.executive_summary.positive_points?.length > 0 && (
                      <div>
                        <h4 className="text-sm font-semibold text-emerald-700 mb-2 flex items-center gap-2">
                          <CheckCircle className="w-4 h-4" />
                          Puntos positivos
                        </h4>
                        <div className="space-y-2">
                          {report.executive_summary.positive_points.map((point, idx) => (
                            <div key={idx} className="flex items-start gap-2 p-3 bg-emerald-50 rounded-sm border border-emerald-100">
                              <CheckCircle className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0 mt-0.5" />
                              <span className="text-sm text-emerald-800">{point}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Concerns / Alerts */}
                    {report.executive_summary.concerns?.length > 0 && (
                      <div>
                        <h4 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2">
                          <AlertTriangle className="w-4 h-4" />
                          Alertas identificadas
                        </h4>
                        <div className="space-y-3">
                          {report.executive_summary.concerns.map((concern, idx) => (
                            <div key={idx} className={`rounded-sm border-l-4 overflow-hidden ${
                              concern.severity === "HIGH" ? "bg-red-50 border-red-500" :
                              concern.severity === "MEDIUM" ? "bg-amber-50 border-amber-500" :
                              "bg-slate-50 border-slate-300"
                            }`}>
                              <div className="p-4">
                                <div className="flex items-center justify-between mb-1">
                                  <h5 className={`text-sm font-semibold ${
                                    concern.severity === "HIGH" ? "text-red-800" :
                                    concern.severity === "MEDIUM" ? "text-amber-800" :
                                    "text-slate-700"
                                  }`}>{concern.title}</h5>
                                  <span className={`text-xs px-2 py-0.5 rounded font-medium ${
                                    concern.severity === "HIGH" ? "bg-red-100 text-red-700" :
                                    concern.severity === "MEDIUM" ? "bg-amber-100 text-amber-700" :
                                    "bg-slate-200 text-slate-600"
                                  }`}>{concern.severity === "HIGH" ? "ALTO" : concern.severity === "MEDIUM" ? "MEDIO" : "BAJO"}</span>
                                </div>
                                <p className="text-sm text-slate-600 leading-relaxed">{concern.detail}</p>
                              </div>

                              {/* Verification Sources */}
                              {(concern.verification_sources?.length > 0 || concern.verification_tip) && (
                                <div className={`px-4 py-3 border-t ${
                                  concern.severity === "HIGH" ? "bg-red-100/50 border-red-200" :
                                  concern.severity === "MEDIUM" ? "bg-amber-100/50 border-amber-200" :
                                  "bg-slate-100/50 border-slate-200"
                                }`}>
                                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
                                    Verificar en:
                                  </p>
                                  {concern.verification_sources?.length > 0 && (
                                    <div className="flex flex-wrap gap-2 mb-2">
                                      {concern.verification_sources.map((src, si) => (
                                        <a
                                          key={si}
                                          href={src.url}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="inline-flex items-center gap-1 text-xs px-2 py-1 bg-white border border-slate-200 rounded hover:bg-[#0b3e65] hover:text-white hover:border-[#0b3e65] transition-colors"
                                          data-testid={`verification-link-${idx}-${si}`}
                                        >
                                          <Globe className="w-3 h-3" />
                                          {src.name}
                                        </a>
                                      ))}
                                    </div>
                                  )}
                                  {concern.verification_tip && (
                                    <p className="text-xs text-slate-500 italic leading-relaxed">
                                      {concern.verification_tip}
                                    </p>
                                  )}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Sources info */}
                    <p className="text-xs text-slate-400 pt-2 border-t border-slate-100">
                      Basado en {report.executive_summary.sources_checked || 0} de {report.executive_summary.sources_total || 0} fuentes de sanciones consultadas.
                      Screening alineado con OFAC 2025 Maritime Advisory.
                    </p>
                  </div>
                </div>
              </section>
            )}

            {/* FUENTES NO CUBIERTAS */}
            {report.executive_summary?.sources_not_covered?.length > 0 && (
              <section data-testid="sources-not-covered-section">
                <div className="flex items-center gap-3 border-b-2 border-amber-500 pb-2 mb-6">
                  <AlertTriangle className="w-5 h-5 text-amber-500" />
                  <h2 className="text-lg font-serif text-amber-700">Fuentes no cubiertas</h2>
                </div>
                <div className="bg-amber-50 border border-amber-200 rounded-sm p-4 mb-4">
                  <p className="text-sm text-amber-800 leading-relaxed">
                    Las siguientes areas de riesgo <strong>no estan evaluadas</strong> en este informe debido a que requieren
                    acceso a herramientas comerciales de pago. Se recomienda complementar este screening con las fuentes indicadas
                    para obtener una evaluacion completa del riesgo.
                  </p>
                </div>
                <div className="space-y-3">
                  {report.executive_summary.sources_not_covered.map((source, idx) => (
                    <div key={idx} className="border border-slate-200 rounded-sm p-4 hover:border-amber-300 transition-colors">
                      <div className="flex items-center justify-between mb-2">
                        <h5 className="text-sm font-semibold text-slate-700">{source.area}</h5>
                        <span className={`text-xs px-2 py-0.5 rounded font-medium ${
                          source.risk_impact === "ALTO" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"
                        }`}>Impacto: {source.risk_impact}</span>
                      </div>
                      <p className="text-sm text-slate-500 mb-2">{source.description}</p>
                      <p className="text-xs text-slate-400">
                        <span className="font-medium">Herramientas requeridas:</span> {source.required_tools}
                      </p>
                    </div>
                  ))}
                </div>
                <div className="mt-4 p-3 bg-slate-50 rounded-sm border border-slate-200">
                  <p className="text-xs text-slate-500">
                    <strong>Nota legal:</strong> Este informe se basa exclusivamente en fuentes de acceso gratuito y publico
                    (Equasis/EMSA, OFAC, ONU, UE, OFSI). Las areas no cubiertas representan riesgos que no han sido evaluados
                    y no deben interpretarse como ausencia de riesgo. Green Bunkers recomienda complementar con herramientas
                    comerciales para operaciones de alto valor o contrapartes desconocidas.
                  </p>
                </div>
              </section>
            )}

            {/* DATA GAP WARNINGS */}
            {report.executive_summary?.data_gap_warnings?.length > 0 && (
              <section data-testid="data-gap-warnings-section">
                <div className="flex items-center gap-3 border-b-2 border-slate-300 pb-2 mb-4">
                  <Info className="w-5 h-5 text-slate-500" />
                  <h2 className="text-lg font-serif text-slate-600">Limitaciones del screening</h2>
                </div>
                <div className="space-y-2">
                  {report.executive_summary.data_gap_warnings.map((warning, idx) => (
                    <div key={idx} className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-100 rounded-sm">
                      <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-amber-800">{warning}</span>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* DATA SOURCES */}
            <section className="border-t border-slate-200 pt-6">
              <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3 flex items-center gap-2">
                <Globe className="w-4 h-4" />
                Data sources consulted (Audit Trail)
              </h3>
              <div className="flex flex-wrap gap-2 text-xs">
                {(report.data_sources_consulted || []).map((source, idx) => (
                  <span key={idx} className="px-2 py-1 bg-slate-100 text-slate-700 rounded">{source}</span>
                ))}
              </div>

              {/* Source statuses */}
              {report.source_statuses?.length > 0 && (
                <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2">
                  {report.source_statuses.map((s, idx) => (
                    <div key={idx} className={`px-2 py-1 text-xs rounded ${
                      s.status === "checked" ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-600"
                    }`}>
                      {s.name}: {s.status === "checked" ? "OK" : "Failed"}
                    </div>
                  ))}
                </div>
              )}

              <p className="text-xs text-slate-400 mt-3">
                Screening aligned with OFAC 2025 Maritime Advisory guidance
              </p>
            </section>
          </div>

          {/* Footer */}
          <div className="bg-slate-50 border-t border-slate-200 p-4 text-center text-xs text-slate-500">
            <p className="font-medium">GREEN BUNKERS COMPLIANCE</p>
            <p className="mt-1">This report is generated for informational purposes only. Always verify information with official sources.</p>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ReportViewPage;
